import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiTrash2, FiFileText, FiX, FiPlus, FiLink, FiFolder } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../utils/api';

const AdminStudyMaterials = () => {
    const [categories, setCategories] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    // Form states
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'link'
    const [externalLink, setExternalLink] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isSeminar, setIsSeminar] = useState(false);
    const [color, setColor] = useState('blue');
    const fileInputRef = useRef(null);

    // Color options
    const colorOptions = [
        { id: 'blue', name: 'Blue', class: 'bg-blue-500' },
        { id: 'red', name: 'Red', class: 'bg-red-500' },
        { id: 'green', name: 'Green', class: 'bg-green-500' },
        { id: 'purple', name: 'Purple', class: 'bg-purple-500' },
        { id: 'yellow', name: 'Yellow', class: 'bg-yellow-500' },
        { id: 'orange', name: 'Orange', class: 'bg-orange-500' },
    ];

    // Category management
    const [newCategoryName, setNewCategoryName] = useState('');
    const [showCategoryForm, setShowCategoryForm] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [categoriesRes, materialsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/study_materials/get_categories.php`, { credentials: 'include' }),
                fetch(`${API_BASE_URL}/study_materials/get_materials.php`, { credentials: 'include' })
            ]);

            const categoriesData = await categoriesRes.json();
            const materialsData = await materialsRes.json();

            if (categoriesData.status === 'success') {
                setCategories(categoriesData.data);
                if (categoriesData.data.length > 0 && !categoryId) {
                    setCategoryId(categoriesData.data[0].id.toString());
                }
            }

            if (materialsData.status === 'success') {
                setMaterials(materialsData.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setSelectedFile(file);
            setPreview(file.name);
        } else {
            toast.error('Please select a valid PDF file');
        }
    };

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryName.trim()) {
            toast.error('Category name is required');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/study_materials/add_category.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: newCategoryName }),
                credentials: 'include'
            });

            const data = await response.json();
            if (data.status === 'success') {
                toast.success('Category added successfully');
                setCategories([...categories, data.data]);
                setNewCategoryName('');
                setShowCategoryForm(false);
            } else {
                toast.error(data.message || 'Failed to add category');
            }
        } catch (error) {
            console.error('Error adding category:', error);
            toast.error('Error adding category');
        }
    };

    const handleDeleteCategory = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"? This will also delete all materials in this category.`)) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/study_materials/delete_category.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
                credentials: 'include'
            });

            const data = await response.json();
            if (data.status === 'success') {
                toast.success('Category deleted');
                setCategories(categories.filter(cat => cat.id !== id));
                setMaterials(materials.filter(mat => mat.category_id !== id));
            } else {
                toast.error(data.message || 'Failed to delete category');
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error('Error deleting category');
        }
    };

    const handleUploadMaterial = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            toast.error('Title is required');
            return;
        }

        if (!categoryId) {
            toast.error('Please select a category');
            return;
        }

        if (uploadMode === 'file' && !selectedFile) {
            toast.error('Please select a PDF file');
            return;
        }

        if (uploadMode === 'link' && !externalLink.trim()) {
            toast.error('Please enter a PDF link');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('category_id', categoryId);
        formData.append('is_seminar', isSeminar);
        if (isSeminar) {
            formData.append('color', color);
        }

        if (uploadMode === 'file') {
            formData.append('pdf_file', selectedFile);
        } else {
            formData.append('external_link', externalLink);
        }

        try {
            const response = await fetch(`${API_BASE_URL}/study_materials/add_material.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            const data = await response.json();
            if (data.status === 'success') {
                toast.success('Material added successfully');
                setTitle('');
                setDescription('');
                setExternalLink('');
                setSelectedFile(null);
                setPreview(null);
                setIsSeminar(false);
                setColor('blue');
                if (fileInputRef.current) fileInputRef.current.value = '';
                fetchData();
            } else {
                toast.error(data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Error uploading material');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteMaterial = async (id, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

        try {
            const response = await fetch(`${API_BASE_URL}/study_materials/delete_material.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id }),
                credentials: 'include'
            });

            const data = await response.json();
            if (data.status === 'success') {
                setMaterials(materials.filter(mat => mat.id !== id));
                toast.success('Material deleted');
            } else {
                toast.error(data.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Error deleting material');
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Study Materials Management</h1>
            </div>

            {/* Category Management Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-semibold text-gray-700">Categories ({categories.length})</h2>
                    <button
                        onClick={() => setShowCategoryForm(!showCategoryForm)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-bold text-sm hover:bg-blue-700 transition-all flex items-center gap-2"
                    >
                        <FiPlus /> Add Category
                    </button>
                </div>

                {showCategoryForm && (
                    <form onSubmit={handleAddCategory} className="mb-6 flex gap-3">
                        <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="Category name"
                            className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            type="submit"
                            className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700"
                        >
                            Add
                        </button>
                        <button
                            type="button"
                            onClick={() => { setShowCategoryForm(false); setNewCategoryName(''); }}
                            className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-bold text-sm hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                    </form>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {categories.map((category) => (
                        <div
                            key={category.id}
                            className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex items-center justify-between group hover:border-blue-400 transition-all"
                        >
                            <div className="flex items-center gap-2">
                                <FiFolder className="text-blue-600" />
                                <span className="font-medium text-gray-700">{category.name}</span>
                            </div>
                            <button
                                onClick={() => handleDeleteCategory(category.id, category.name)}
                                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-all"
                            >
                                <FiTrash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Upload Material Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-700 mb-4">Add New Material</h2>

                {/* Upload Mode Toggle */}
                <div className="flex gap-4 mb-6">
                    <button
                        onClick={() => setUploadMode('file')}
                        className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${uploadMode === 'file'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        <FiUpload /> Upload PDF File
                    </button>
                    <button
                        onClick={() => setUploadMode('link')}
                        className={`flex-1 py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${uploadMode === 'link'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        <FiLink /> Add External Link
                    </button>
                </div>

                <form onSubmit={handleUploadMaterial} className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter material title"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                            <select
                                value={categoryId}
                                onChange={(e) => setCategoryId(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            >
                                <option value="">Select Category</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                            placeholder="Enter description (optional)"
                        ></textarea>
                    </div>

                    <div className="flex flex-col gap-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isSeminar}
                                onChange={(e) => setIsSeminar(e.target.checked)}
                                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                            <span className="text-sm font-bold text-gray-700">Mark as Online Free Live Session</span>
                        </label>

                        {isSeminar && (
                            <div className="pl-8">
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Display Color</label>
                                <div className="flex flex-wrap gap-2">
                                    {colorOptions.map(opt => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            onClick={() => setColor(opt.id)}
                                            className={`w-8 h-8 rounded-full ${opt.class} ring-offset-2 transition-all ${color === opt.id ? 'ring-2 ring-blue-500 scale-110' : 'hover:scale-110'}`}
                                            title={opt.name}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {uploadMode === 'file' ? (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">PDF File *</label>
                            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-blue-400 transition-all">
                                {preview ? (
                                    <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <FiFileText className="text-blue-600" />
                                            <span className="text-sm text-gray-700">{preview}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setSelectedFile(null); setPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FiX />
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <FiFileText className="text-4xl text-gray-400 mx-auto mb-2" />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current.click()}
                                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium"
                                        >
                                            Choose PDF File
                                        </button>
                                        <p className="text-xs text-gray-400 mt-2">Max 10MB</p>
                                    </div>
                                )}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept="application/pdf"
                                    onChange={handleFileSelect}
                                />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {isSeminar ? 'Zoom Meeting Link *' : 'External Link (PDF or Zoom) *'}
                            </label>
                            <input
                                type="url"
                                value={externalLink}
                                onChange={(e) => setExternalLink(e.target.value)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder={isSeminar ? "https://zoom.us/j/..." : "https://example.com/file.pdf"}
                                required={uploadMode === 'link'}
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={uploading}
                        className={`w-full py-3 rounded-lg font-bold text-white transition-all shadow-lg flex items-center justify-center gap-2 ${uploading
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/25'
                            }`}
                    >
                        {uploading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Uploading...
                            </>
                        ) : (
                            <>
                                <FiPlus /> Add Material
                            </>
                        )}
                    </button>
                </form>
            </div>

            {/* Materials List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-700 mb-6">Uploaded Materials ({materials.length})</h2>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                ) : materials.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <FiFileText className="text-4xl mx-auto mb-3 opacity-50" />
                        <p>No materials uploaded yet</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <AnimatePresence>
                            {materials.map((material) => (
                                <motion.div
                                    key={material.id}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.5 }}
                                    layout
                                    className="group bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-blue-400 transition-all"
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className={`p-2 rounded-lg ${material.is_seminar ? `bg-${material.color || 'blue'}-100 text-${material.color || 'blue'}-600` : 'bg-blue-100 text-blue-600'}`}>
                                            <FiFileText />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-bold text-gray-800 text-sm truncate">{material.title}</h3>
                                                {material.is_seminar ? (
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold bg-${material.color || 'blue'}-100 text-${material.color || 'blue'}-600 uppercase`}>Live Session</span>
                                                ) : null}
                                            </div>
                                            <span className="text-xs text-gray-500 font-medium">{material.category_name}</span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteMaterial(material.id, material.title)}
                                            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-all"
                                        >
                                            <FiTrash2 size={16} />
                                        </button>
                                    </div>
                                    {material.description && (
                                        <p className="text-xs text-gray-600 mb-2 line-clamp-2">{material.description}</p>
                                    )}
                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                        <span>{new Date(material.created_at).toLocaleDateString()}</span>
                                        {material.external_link ? (
                                            <span className="text-blue-600 flex items-center gap-1">
                                                <FiLink size={12} /> Link
                                            </span>
                                        ) : (
                                            <span>{(material.file_size / (1024 * 1024)).toFixed(2)} MB</span>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminStudyMaterials;
